/**
 * Gateway agent loop runner.
 *
 * One `runGatewayTurn(opts)` call executes one turn of conversation:
 *   1. Build the message stack (system prompt + context + history + user msg)
 *   2. Loop: stream from /api/ai/chat/stream → dispatch tool calls as they
 *      complete → push assistant + tool results into history → next round
 *   3. Exit when the model calls `final_answer`, ends a round with no tool
 *      calls, hits the iteration cap, or the user aborts.
 *
 * Slimmer than the cseditor's loopRunner — no undo wrapping, no post-mutation
 * layout validation, no sub-agent nesting. The gateway's mutating tools just
 * dispatch CustomEvents that section pages listen for; nothing here needs to
 * be reversed at turn end.
 */

import { streamLLM } from "./aiService";
import { decodeStringEscapes } from "./tools/meta";

// 25 was too tight for multi-step flows like "open modal + inspect + fill
// fields + verify + final_answer" — bumped to 40 with set_fields batching
// most form-fill rounds out of the equation. WALL_CLOCK_MS is the absolute
// cap regardless of iteration count.
const MAX_ITERATIONS = 40;
const WALL_CLOCK_MS = 240_000;
// If the model calls the same tool with the same arguments this many times
// in a row, we inject a loop-break hint so it changes strategy instead of
// silently chewing up iterations.
const LOOP_DETECT_REPEAT_THRESHOLD = 3;

function isPlainObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/**
 * Some models stream tool calls as raw text instead of using the structured
 * tool_calls API:
 *   - `final_answer({...})`  (Python/JS literal syntax)
 *   - `{"name":"final_answer", "arguments":{...}}`  (JSON form)
 *   - `<function_calls><invoke name="final_answer"><argument name="text">…
 *      </argument></invoke></function_calls>`  (Claude XML form)
 *
 * When that happens we end up with the rendered text + a call expression in
 * the message content. This pulls the inner `text` field out so the user
 * sees only the prose.
 *
 * Returns the extracted text, or null if no inline call was found.
 */
function extractInlineFinalAnswer(content) {
  if (typeof content !== "string" || !content) return null;
  // Pattern A: literal `final_answer(<json>)` somewhere in the content.
  // The inline JSON may itself contain braces inside string values, so we
  // brace-balance starting from the first `{` after the call name.
  const callIdx = content.search(/final_answer\s*\(\s*\{/);
  if (callIdx !== -1) {
    const startBrace = content.indexOf("{", callIdx);
    const json = sliceBalancedBraces(content, startBrace);
    const parsed = safeJsonParse(json);
    if (parsed && typeof parsed.text === "string") return parsed.text;
  }
  // Pattern B: top-level JSON object with name: "final_answer".
  const nameIdx = content.search(/\{[^{}]*"name"\s*:\s*"final_answer"/);
  if (nameIdx !== -1) {
    const json = sliceBalancedBraces(content, nameIdx);
    const parsed = safeJsonParse(json);
    const text =
      (typeof parsed?.arguments?.text === "string" && parsed.arguments.text) ||
      (typeof parsed?.parameters?.text === "string" && parsed.parameters.text) ||
      (typeof parsed?.text === "string" && parsed.text) ||
      null;
    if (text) return text;
  }
  // Pattern C: Claude-style XML invocation. `<argument>` and `<parameter>`
  // both appear in the wild — match either.
  const xml = content.match(
    /<invoke[^>]*\bname=["']final_answer["'][^>]*>[\s\S]*?<(?:argument|parameter)[^>]*\bname=["']text["'][^>]*>([\s\S]*?)<\/(?:argument|parameter)>[\s\S]*?<\/invoke>/i
  );
  if (xml && typeof xml[1] === "string" && xml[1].trim()) {
    return decodeHtmlEntities(xml[1].trim());
  }
  return null;
}

/**
 * Strip the structural noise the model occasionally bakes into prose when it
 * goes off-protocol — XML invocations and the literal `final_answer({...})`
 * form. These are the patterns that produce visibly broken UI (raw markup,
 * function-call literals); the prompt covers the softer cases like
 * "navigate_to(...)" mentioned in passing.
 */
function stripInlineToolCallNoise(content) {
  if (typeof content !== "string" || !content) return "";
  let s = content;
  s = s.replace(/<function_calls\b[^>]*>[\s\S]*?<\/function_calls>/gi, "");
  s = s.replace(/<invoke\b[\s\S]*?<\/invoke>/gi, "");
  s = s.replace(/<tool_call\b[\s\S]*?<\/tool_call>/gi, "");
  s = s.replace(/(?:^|\s)final_answer\s*\(\s*\{[\s\S]*?\}\s*\)\s*$/g, "");
  s = s.replace(/\{\s*"name"\s*:\s*"final_answer"[\s\S]*?\}\s*$/g, "");
  return s.trim();
}

function decodeHtmlEntities(s) {
  if (typeof s !== "string") return "";
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
}

/**
 * Coerce a raw text node value into a JS value before passing to a tool.
 * The model's XML form encodes everything as text, but the registry's JSON
 * Schema validator expects typed values for non-string fields. Best-effort
 * decoding keeps validation passing without us forcing every tool to accept
 * stringly-typed inputs.
 */
function parseArgValue(text) {
  if (typeof text !== "string") return text;
  const decoded = decodeHtmlEntities(text);
  const trimmed = decoded.trim();
  if (trimmed === "") return decoded;
  // JSON object/array — usually how the model encodes nested args like
  // `mutation` for request_confirmation.
  if (/^[{[]/.test(trimmed)) {
    try {
      return JSON.parse(trimmed);
    } catch {
      // fall through
    }
  }
  // Pure numbers — coerce so JSON Schema "integer"/"number" validators pass.
  if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) return Number(trimmed);
  // Don't auto-coerce "true"/"false"/"null" to typed JS values: tools that
  // need booleans (set_field on a checkbox) accept the string forms, and
  // a text field set to the literal word "true" was a real production bug.
  return decoded;
}

/**
 * Parse Claude-style inline XML tool calls out of streamed text content.
 * Returns one synthesized call per `<invoke>` block found.
 *
 *   <function_calls>
 *     <invoke name="highlight_element">
 *       <parameter name="selector">.gwAIButton</parameter>
 *     </invoke>
 *     <invoke name="final_answer">
 *       <argument name="text">…</argument>
 *     </invoke>
 *   </function_calls>
 */
export function parseInlineXMLToolCalls(content) {
  if (typeof content !== "string" || !content) return [];
  const calls = [];
  const invokeRe =
    /<invoke\b[^>]*\bname=["']([^"']+)["'][^>]*>([\s\S]*?)<\/invoke>/gi;
  let m;
  while ((m = invokeRe.exec(content)) !== null) {
    const name = m[1];
    const inner = m[2];
    const args = {};
    const argRe =
      /<(?:argument|parameter)\b[^>]*\bname=["']([^"']+)["'][^>]*>([\s\S]*?)<\/(?:argument|parameter)>/gi;
    let am;
    while ((am = argRe.exec(inner)) !== null) {
      args[am[1]] = parseArgValue(am[2]);
    }
    calls.push({ name, args });
  }
  return calls;
}

/**
 * Parse a single literal-form tool call from text:
 *   - `name({"text": "..."})`              (Python/JS literal)
 *   - `{"name": "...", "arguments": {...}}` (JSON form)
 *
 * Returns the LAST candidate at end of content (so leading prose like
 * "if you ask me final_answer({...})" doesn't accidentally match).
 */
/**
 * Best-effort: parse all inline tool calls from the model's text output,
 * preferring XML form (which can carry multiple calls) over literal form
 * (which is one call max). Returns [] when nothing parses.
 */
export function collectInlineToolCalls(content) {
  const xml = parseInlineXMLToolCalls(content);
  if (xml.length > 0) return xml;
  const lit = parseInlineLiteralToolCall(content);
  return lit ? [lit] : [];
}

export function parseInlineLiteralToolCall(content) {
  if (typeof content !== "string" || !content) return null;

  // Pattern A: `name(...)` literal, prefer the last one.
  const callRe = /([a-zA-Z_][a-zA-Z0-9_]*)\s*\(\s*\{/g;
  let last = null;
  let m;
  while ((m = callRe.exec(content)) !== null) last = m;
  if (last) {
    const name = last[1];
    const startBrace = last.index + last[0].length - 1;
    const json = sliceBalancedBraces(content, startBrace);
    const parsed = safeJsonParse(json);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return { name, args: parsed };
    }
  }

  // Pattern B: top-level JSON {name, arguments|parameters}.
  const idx = content.search(/\{[^{}]*"name"\s*:\s*"[^"]+"/);
  if (idx !== -1) {
    const json = sliceBalancedBraces(content, idx);
    const parsed = safeJsonParse(json);
    if (parsed && typeof parsed.name === "string") {
      const args =
        (parsed.arguments && typeof parsed.arguments === "object" && parsed.arguments) ||
        (parsed.parameters && typeof parsed.parameters === "object" && parsed.parameters) ||
        {};
      return { name: parsed.name, args };
    }
  }
  return null;
}

/**
 * Walk forward from `start` (a `{` index), tracking string state and brace
 * depth, and return the substring covering the matching closing `}`. Returns
 * an empty string if `start` isn't a `{` or no matching brace is found.
 */
function sliceBalancedBraces(s, start) {
  if (typeof s !== "string" || s[start] !== "{") return "";
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inString) {
      if (escape) escape = false;
      else if (ch === "\\") escape = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return s.slice(start, i + 1);
    }
  }
  return "";
}

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/**
 * Order-stable JSON.stringify so two arg objects with the same content
 * produce the same string regardless of key order. Used by the loop
 * detector — without sorted keys, `{a:1,b:2}` and `{b:2,a:1}` would look
 * like different calls and slip past detection.
 */
function stableArgsString(args) {
  if (args == null || typeof args !== "object") {
    try { return JSON.stringify(args); } catch { return String(args); }
  }
  const keys = Object.keys(args).sort();
  const out = {};
  for (const k of keys) out[k] = args[k];
  try { return JSON.stringify(out); } catch { return String(args); }
}

function isCompleteJsonObject(s) {
  if (typeof s !== "string" || !s) return false;
  const trimmed = s.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return false;
  try {
    return isPlainObject(JSON.parse(trimmed));
  } catch {
    return false;
  }
}

function parseToolArgs(argsChunks) {
  const argsStr = argsChunks.join("");
  if (!argsStr) return { parsed: {}, parseError: false };
  try {
    return { parsed: JSON.parse(argsStr), parseError: false };
  } catch {}
  // Walk chunks in reverse — last chunk that strict-parses wins (for adapters
  // that emit cumulative snapshots rather than incremental deltas).
  for (let i = argsChunks.length - 1; i >= 0; i--) {
    try {
      const parsed = JSON.parse(argsChunks[i]);
      if (isPlainObject(parsed)) return { parsed, parseError: false };
    } catch {}
  }
  return { parsed: { __parseError: true, __raw: argsStr }, parseError: true };
}

async function runOneRound({ messages, registry, signal, emit }) {
  const stream = streamLLM({
    messages,
    tools: registry.schemas(),
    signal,
  });

  // stream key → { id, name, argsChunks[], dispatched, parsedArgs, result }
  const toolBuffer = new Map();
  let textBuffer = "";
  let finishReason = null;
  let providerState = "";
  let lastToolKey = null;
  let nextToolOrdinal = 0;

  const findToolKeyByID = (id) => {
    if (!id) return null;
    for (const [key, entry] of toolBuffer.entries()) {
      if (entry.id === id) return key;
    }
    return null;
  };

  const resolveToolKey = (tc) => {
    if (typeof tc.index === "number") return `index:${tc.index}`;
    if (tc.id) return findToolKeyByID(tc.id) || `id:${tc.id}`;
    if (lastToolKey !== null) return lastToolKey;
    return `unindexed:${nextToolOrdinal}`;
  };

  const sortedToolEntries = () =>
    [...toolBuffer.entries()].sort(([, a], [, b]) => {
      if (typeof a.streamIndex === "number" && typeof b.streamIndex === "number") {
        return a.streamIndex - b.streamIndex || a.ordinal - b.ordinal;
      }
      return a.ordinal - b.ordinal;
    });

  const tryDispatch = async (key, { force = false } = {}) => {
    const entry = toolBuffer.get(key);
    if (!entry || entry.dispatched || !entry.id || !entry.name) return;
    const { parsed, parseError } = parseToolArgs(entry.argsChunks);
    if (parseError && !force) return;
    entry.dispatched = true;
    entry.parsedArgs = parsed;
    // args ride along so the conversation log can record what the tool was
    // actually asked to do — the status line ignores them.
    emit({ type: "tool_start", id: entry.id, name: entry.name, args: parsed });
    if (parseError) {
      entry.result = await registry.dispatch(entry.name, parsed);
      if (entry.result?.ok === false && entry.result.error) {
        entry.result = {
          ...entry.result,
          error: {
            ...entry.result.error,
            hint:
              "Your tool-call arguments were not valid JSON — re-emit the call with strict JSON.",
          },
        };
      }
    } else {
      entry.result = await registry.dispatch(entry.name, parsed);
    }
    emit({ type: "tool_result", id: entry.id, name: entry.name, result: entry.result });
  };

  for await (const chunk of stream) {
    if (chunk?.error && typeof chunk.error === "string") throw new Error(chunk.error);
    if (
      typeof chunk?.cs_provider_state === "string" &&
      chunk.cs_provider_state.trim() !== ""
    ) {
      providerState = chunk.cs_provider_state;
    }
    const choice = chunk?.choices?.[0];
    if (!choice) continue;
    const delta = choice.delta || {};

    if (typeof delta.content === "string" && delta.content) {
      if (lastToolKey !== null) await tryDispatch(lastToolKey);
      textBuffer += delta.content;
      emit({ type: "text", delta: delta.content });
    }

    if (Array.isArray(delta.tool_calls)) {
      for (const tc of delta.tool_calls) {
        const key = resolveToolKey(tc);
        if (lastToolKey !== null && key !== lastToolKey) {
          await tryDispatch(lastToolKey);
        }
        lastToolKey = key;

        let entry = toolBuffer.get(key);
        if (!entry) {
          entry = {
            id: null,
            name: null,
            argsChunks: [],
            dispatched: false,
            streamIndex: typeof tc.index === "number" ? tc.index : null,
            ordinal: nextToolOrdinal++,
            // Provider extras (Gemini thought signatures) that must be
            // echoed back verbatim when this call is replayed in history.
            extraContent: null,
          };
          toolBuffer.set(key, entry);
        }
        if (tc.id && !entry.id) entry.id = tc.id;
        if (tc.function?.name && !entry.name) entry.name = tc.function.name;
        if (tc.extra_content && !entry.extraContent) entry.extraContent = tc.extra_content;

        const frag = tc.function?.arguments;
        if (frag && typeof frag === "object") {
          // Some adapters deliver function.arguments as a parsed object
          // rather than a JSON string. Treat it as the complete snapshot.
          try {
            entry.argsChunks = [JSON.stringify(frag)];
          } catch {}
        } else if (typeof frag === "string") {
          const existing = entry.argsChunks.join("");
          if (!existing) {
            entry.argsChunks.push(frag);
          } else if (frag === existing) {
            // duplicate snapshot — skip
          } else if (isCompleteJsonObject(frag)) {
            entry.argsChunks = [frag];
          } else if (frag.length > existing.length && frag.startsWith(existing)) {
            entry.argsChunks = [frag];
          } else {
            entry.argsChunks.push(frag);
          }
        }
      }
    }
    if (choice.finish_reason) finishReason = choice.finish_reason;
  }

  // Stream closed — force-dispatch pending calls in stable provider order.
  const undispatched = sortedToolEntries().filter(
    ([, entry]) => !entry.dispatched && entry.id && entry.name,
  );
  for (const [key] of undispatched) {
    if (signal?.aborted) break;
    await tryDispatch(key, { force: true });
  }

  // Resilience layer: if the model emitted tool calls as TEXT (Claude XML,
  // `name({...})` literal, JSON form) instead of using the structured
  // tool_calls API, parse them out of textBuffer and dispatch them. This is
  // what lets `highlight_element`, `navigate_to`, etc. actually take effect
  // even when the model goes off-protocol — without this, the user just
  // sees a sentence claiming the action ran while nothing actually happened.
  //
  // Only runs when toolBuffer is empty: if the model used the real API, the
  // structured calls win and we don't double-execute anything they also
  // narrated as text.
  if (toolBuffer.size === 0 && textBuffer && !signal?.aborted) {
    const inlineCalls = collectInlineToolCalls(textBuffer);
    let synth = 0;
    for (const ic of inlineCalls) {
      if (signal?.aborted) break;
      if (typeof ic?.name !== "string" || !ic.name) continue;
      if (!registry.has(ic.name)) continue;
      const id = `inline_${synth}_${Date.now().toString(36)}`;
      const entry = {
        id,
        name: ic.name,
        argsChunks: [JSON.stringify(ic.args || {})],
        dispatched: true,
        parsedArgs: ic.args || {},
        streamIndex: null,
        ordinal: nextToolOrdinal++,
      };
      emit({ type: "tool_start", id, name: ic.name, args: ic.args || {} });
      entry.result = await registry.dispatch(ic.name, ic.args || {});
      emit({ type: "tool_result", id, name: ic.name, result: entry.result });
      toolBuffer.set(`inline:${synth}`, entry);
      synth++;
    }
    if (synth > 0) {
      // Strip the parsed XML/literals from the text so the assistant
      // message doesn't contain both the call and a structured tool_calls
      // entry. The model will see clean text + structured calls next round
      // — exactly the shape it should have produced in the first place.
      textBuffer = stripInlineToolCallNoise(textBuffer);
    }
  }

  // Build assistant message + ordered tool calls.
  const sortedEntries = sortedToolEntries();
  const toolCalls = [];
  const assistantToolCalls = [];
  for (const [, entry] of sortedEntries) {
    if (!entry.id || !entry.name) continue;
    const argsOK = entry.parsedArgs && !entry.parsedArgs.__parseError;
    const canonicalArgs = argsOK ? JSON.stringify(entry.parsedArgs) : "{}";
    toolCalls.push({
      id: entry.id,
      name: entry.name,
      args: argsOK ? entry.parsedArgs : {},
      result: entry.result,
    });
    assistantToolCalls.push({
      id: entry.id,
      type: "function",
      function: { name: entry.name, arguments: canonicalArgs },
      ...(entry.extraContent ? { extra_content: entry.extraContent } : {}),
    });
  }

  const assistantMessage = { role: "assistant" };
  if (textBuffer) assistantMessage.content = textBuffer;
  else if (assistantToolCalls.length === 0) assistantMessage.content = "";
  else assistantMessage.content = null;
  if (assistantToolCalls.length > 0) assistantMessage.tool_calls = assistantToolCalls;
  if (providerState.trim() !== "") assistantMessage.provider_state = providerState;

  return { assistantMessage, toolCalls, finishReason };
}

/**
 * @param {Object} opts
 * @param {Array}  opts.messages    Prepared OpenAI messages (system + user/asst)
 * @param {Object} opts.registry    Tool registry (build via buildToolRegistry)
 * @param {AbortSignal} [opts.signal]
 * @param {(evt:any)=>void} [opts.onEvent]
 * @returns {Promise<{
 *   ok: boolean,
 *   reason: 'stop'|'max_iter'|'wall_time'|'aborted'|'error',
 *   history: Array,
 *   finalAnswerText: string,
 *   error?: string,
 * }>}
 */
export async function runGatewayTurn({ messages, registry, signal, onEvent } = {}) {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error("runGatewayTurn: messages required");
  }
  if (!registry || typeof registry.dispatch !== "function") {
    throw new Error("runGatewayTurn: registry required");
  }
  const emit = (evt) => {
    if (typeof onEvent === "function") {
      try {
        onEvent(evt);
      } catch {}
    }
  };

  const startedAt = Date.now();
  const conv = [...messages];
  let ok = true;
  let reason = "stop";
  let finalAnswerText = "";
  let hardError = null;

  // Loop detection — track the last few tool calls keyed by name+args. If
  // the same call repeats N+ times in a row, we inject a one-shot system
  // hint nudging the model to change strategy. Without this, a confused
  // model can chew through every iteration calling the same tool.
  const recentCalls = [];
  let loopHintInjected = false;

  // Failure-streak detection. Loop detection only catches identical args
  // — but Grok's failure mode in the wild was different: it kept calling
  // `navigate_to` with VARYING wrong args (`{text: "..."}`). Each call had
  // a unique signature, so the loop detector never fired. This counter
  // tracks consecutive INVALID_ARGS for the same tool name and dumps the
  // schema (verbatim) into the conversation when it gets stuck.
  const consecutiveFailures = new Map(); // toolName → count
  const schemaHintInjectedFor = new Set();

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    if (signal?.aborted) {
      reason = "aborted";
      break;
    }
    if (Date.now() - startedAt > WALL_CLOCK_MS) {
      reason = "wall_time";
      break;
    }

    let round;
    try {
      round = await runOneRound({ messages: conv, registry, signal, emit });
    } catch (err) {
      if (err?.name === "AbortError" || signal?.aborted) {
        reason = "aborted";
        break;
      }
      hardError = err?.message || String(err);
      ok = false;
      reason = "error";
      emit({ type: "error", message: hardError });
      break;
    }

    conv.push(round.assistantMessage);

    let finalAnswerCall = null;
    let pendingConfirmationCall = null;
    for (const call of round.toolCalls) {
      if (call.name === "final_answer") finalAnswerCall = call;
      if (
        call.result &&
        call.result.ok === false &&
        call.result.error &&
        call.result.error.code === "PENDING_CONFIRMATION" &&
        !pendingConfirmationCall
      ) {
        pendingConfirmationCall = call;
      }
      conv.push({
        role: "tool",
        tool_call_id: call.id,
        content: JSON.stringify(call.result ?? { ok: false, error: { code: "NO_RESULT" } }),
      });
    }

    if (signal?.aborted) {
      reason = "aborted";
      break;
    }

    if (pendingConfirmationCall) {
      // The AI proposed a mutation; the user needs to Approve/Reject.
      // Stop the turn here so the popover can render the inline card. The
      // popover will run the mutation (or send Declined) and start a new
      // turn with the result.
      const err = pendingConfirmationCall.result.error;
      reason = "awaiting_confirmation";
      finalAnswerText = "";
      emit({
        type: "pending_confirmation",
        confirmationToken: err.confirmationToken,
        proposed: err.proposed,
        expiresAt: err.expiresAt,
        toolUseId: pendingConfirmationCall.id,
      });
      return {
        ok: false,
        reason,
        history: conv,
        finalAnswerText: "",
        pendingConfirmation: {
          confirmationToken: err.confirmationToken,
          proposed: err.proposed,
          expiresAt: err.expiresAt,
          toolUseId: pendingConfirmationCall.id,
        },
      };
    }

    if (finalAnswerCall) {
      const text =
        typeof finalAnswerCall.result?.data?.text === "string"
          ? finalAnswerCall.result.data.text
          : "";
      finalAnswerText = text;
      emit({ type: "final_answer", text });
      reason = "stop";
      break;
    }

    // Loop detection: if the model just called the same tool with the same
    // args N+ rounds in a row, inject a hint and let it self-correct.
    for (const call of round.toolCalls) {
      if (call.name === "final_answer") continue;
      const sig = `${call.name}:${stableArgsString(call.args)}`;
      recentCalls.push(sig);
    }

    // Failure-streak: if the same tool keeps returning INVALID_ARGS even
    // with varying args, the model is confused about the schema. Dump the
    // schema verbatim into the conversation so it can read what's required.
    let toolToBan = null;
    for (const call of round.toolCalls) {
      if (call.name === "final_answer") continue;
      const isValidationFailure =
        call.result?.ok === false &&
        (call.result?.error?.code === "INVALID_ARGS" ||
          call.result?.error?.code === "INVALID_MUTATION_ARGS");
      if (isValidationFailure) {
        const count = (consecutiveFailures.get(call.name) || 0) + 1;
        consecutiveFailures.set(call.name, count);
        if (count >= 2 && !schemaHintInjectedFor.has(call.name)) {
          schemaHintInjectedFor.add(call.name);
          const schema = registry.schemas().find(
            (s) => s.function?.name === call.name
          );
          const schemaText = schema
            ? JSON.stringify(schema.function.parameters, null, 2)
            : "(schema unavailable)";
          conv.push({
            role: "system",
            content:
              `You've called \`${call.name}\` with invalid args ${count} times. ` +
              `Read its parameter schema carefully:\n\n\`\`\`json\n${schemaText}\n\`\`\`\n\n` +
              `If you wanted to send a text reply, the tool you want is \`final_answer({text: "..."})\` — NOT \`${call.name}\`. ` +
              `Match your args EXACTLY to the schema above on your next attempt, or call a different tool.`,
          });
        }
        // Bail-out: 5+ consecutive failures of the same tool means the
        // model is genuinely stuck. Force a final_answer fallback so we
        // don't burn iterations with no end in sight.
        if (count >= 5) {
          toolToBan = call.name;
        }
      } else {
        // Reset counter on any successful (or non-INVALID_ARGS) call.
        consecutiveFailures.set(call.name, 0);
      }
    }
    if (toolToBan) {
      conv.push({
        role: "system",
        content:
          `STOP calling \`${toolToBan}\`. You've failed 5 times in a row — it's not going to start working. ` +
          `Call \`final_answer\` NOW and tell the user honestly: "I had trouble using \`${toolToBan}\` — could you do it manually?" Do not retry.`,
      });
      // Don't break the loop here; the model still needs a turn to emit
      // final_answer. The forceful hint above plus the loop's normal
      // termination on final_answer should resolve it within 1-2 rounds.
    }
    if (recentCalls.length > LOOP_DETECT_REPEAT_THRESHOLD) {
      recentCalls.splice(0, recentCalls.length - LOOP_DETECT_REPEAT_THRESHOLD);
    }
    const looping =
      !loopHintInjected &&
      recentCalls.length >= LOOP_DETECT_REPEAT_THRESHOLD &&
      recentCalls.every((s) => s === recentCalls[0]);
    if (looping) {
      loopHintInjected = true;
      conv.push({
        role: "system",
        content:
          "You've called the same tool with the same arguments " +
          `${LOOP_DETECT_REPEAT_THRESHOLD} times in a row. The result isn't ` +
          "going to change on retry. Either call a DIFFERENT tool, change " +
          "the arguments, or call `final_answer` to wrap up and tell the " +
          "user what you learned so far.",
      });
    }

    if (round.toolCalls.length === 0) {
      // Off-protocol: model didn't end with final_answer. Some models emit
      // the tool call as a TEXT representation in their streamed content
      // (e.g. literal `final_answer({"text":"..."})` or XML `<invoke …>`)
      // instead of using the structured tool_calls API. Extract the inner
      // text when we can; either way, strip any leaked tool-call noise so
      // the user never sees `<function_calls>` etc. in their reply.
      const rawContent =
        typeof round.assistantMessage?.content === "string"
          ? round.assistantMessage.content
          : "";
      const extracted = extractInlineFinalAnswer(rawContent);
      const fallbackText = extracted || stripInlineToolCallNoise(rawContent);
      // Decode escape sequences so any literal `\n`/`\t` left over from
      // a double-escaping adapter renders as real whitespace. No effect on
      // text that already had real newlines.
      finalAnswerText = decodeStringEscapes(fallbackText);
      emit({ type: "final_answer", text: finalAnswerText });
      reason = "stop";
      break;
    }

    if (iter === MAX_ITERATIONS - 1) {
      reason = "max_iter";
    }
  }

  emit({ type: "finish", reason });
  return {
    ok: ok && reason === "stop",
    reason,
    history: conv,
    finalAnswerText: finalAnswerText || "",
    ...(hardError ? { error: hardError } : {}),
  };
}
